import React from "react";
import {
  START_DATE,
  HOST_NATION,
  WINNER,
  RUNNER_UP
} from "../WhatsOnTable/types";

import { SortCriteria } from "../WhatsOnHero/types";
import { formatDate, omitNation } from "../../helpers/utils";
import { dropdownItem } from "packages/static/Dropdown/types";

interface TableProps {
  items?: any[];
  className?: string;
  tourCode: string;
  setSortCriteria: (criteria: SortCriteria) => void;
  sortCriteria: SortCriteria;
  matchType: dropdownItem;
  matchTypeCode: string;
}

interface TournamentProps {
  tourStatusCode: string;
  isRecognised: string;
  tournamentLink: string;
  name: string;
  recognisedTournamentLink: string;
  hostNationCode: string;
  hostNation: string;
  tourStatusDesc: string;
  liveStreamingUrl: string;
  liveLink: string;
  dates: string;
  location: string;
  category: string;
  prizeMoney: string;
  indoorOrOutDoor: string;
  surfaceDesc: string;
  startDate: string;
  endDate: string;

  opponent1FamilyName: string;
  opponent1GivenName: string;
  opponent1NationalityCode: string;
  opponent1PlayerId: number;
  opponent1ProfileLink: string;

  opponent2GivenName: string;
  opponent2FamilyName: string;
  opponent2NationalityCode: string;
  opponent2PlayerId: number;
  opponent2ProfileLink: string;

  playerFamilyName: string;
  playerGivenName: string;
  playerNationalityCode: string;
  playerId: number;
  playerProfileLink: string;

  partnerFamilyName: string | null;
  partnerGivenName: string | null;
  partnerNationalityCode: string | null;
  partnerPlayerId: number | null;
  partnerProfileLink: string;

  matchTypeCode: string;

  score: string | null;
  isWinner: boolean;

  winner?: any[];
  opponent?: any[];

  matches: any[];
  results: any[];
}

const MobileView = ({ matches }: { matches: TournamentProps[] }) => {
  const games = matches.map(match => {
    return [match.winner, match.opponent];
  });

  return (
    <td className="winner winner__mobile-view">
      <div className="winner">
        <span className="winner-name">
          {games
            ? games.map((teams: any, key: number) => {
                return (
                  <div key={key} className="winner-name__item-container">
                    {teams.map((team: any, key: number) => {
                      return (
                        <div
                          key={key}
                          className={`team${
                            key === 0 ? " winner" : " runner-up"
                          }`}
                        >
                          {team.map((player: any, key: number) => {
                            return (
                              <div key={key} className="winner-name__item">
                                <div
                                  className={`itf-flags itf-flags--${player.nationalityCode}`}
                                />
                                <div>{`${player.givenName.substring(0, 1)}. ${
                                  player.familyName
                                } `}</div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                );
              })
            : null}
        </span>
      </div>
    </td>
  );
};

const Status = ({ code }: any) => {
  switch (code) {
    case "CN":
      return (
        <span className="status">
          <div className="cancelled">Cancelled</div>
        </span>
      );
      break;
    case "PP":
      return (
        <span className="status">
          <div className="postponed">Postponed</div>
        </span>
      );
      break;
    default:
      return <span className="status" />;
  }
};

interface Player {
  familyName: string;
  givenName: string;
  nationalityCode: string;
  playerID: string;
  playerProfileLink: string;
}

interface MatchData {
  winner: Player[];
  opponent: Player[];
  score: string;
  matchTypeCode: string;
}

const Table = ({
  items = [],
  className,
  tourCode,
  setSortCriteria,
  sortCriteria,
  matchTypeCode
}: TableProps) => {
  const data = items.map(item => {
    let matches: any = item.results
      .map((match: TournamentProps) => ({
        isWinner: match.isWinner,
        team1: [
          {
            familyName: match.playerFamilyName,
            givenName: match.playerGivenName,
            nationalityCode: match.playerNationalityCode,
            playerID: match.playerId,
            playerProfileLink: match.playerProfileLink
          },
          {
            familyName: match.partnerFamilyName,
            givenName: match.partnerGivenName,
            nationalityCode: match.partnerNationalityCode,
            playerID: match.partnerPlayerId,
            playerProfileLink: match.partnerProfileLink
          }
        ],
        team2: [
          {
            familyName: match.opponent1FamilyName,
            givenName: match.opponent1GivenName,
            nationalityCode: match.opponent1NationalityCode,
            playerID: match.opponent1PlayerId,
            playerProfileLink: match.opponent1ProfileLink
          },
          {
            familyName: match.opponent2FamilyName,
            givenName: match.opponent2GivenName,
            nationalityCode: match.opponent2NationalityCode,
            playerID: match.opponent2PlayerId,
            playerProfileLink: match.opponent2ProfileLink
          }
        ],
        score: match.score,
        matchTypeCode: match.matchTypeCode
      }))
      .map((match: any) => {
        let newMatch = {
          ...match,
          team1: match.team1.filter((winner: any) => winner.playerID),
          team2: match.team2.filter((opp: any) => opp.playerID)
        };
        return newMatch;
      })
      .map((match: any) => {
        return {
          winner: match.isWinner ? match.team1 : match.team2,
          opponent: !match.isWinner ? match.team1 : match.team2,
          score: match.score,
          matchTypeCode: match.matchTypeCode
        };
      });

    let opponent = [
      {
        familyName: item.opponent1FamilyName,
        givenName: item.opponent1GivenName,
        nationalityCode: item.opponent1NationalityCode,
        playerID: item.opponent1PlayerID
      }
    ];

    let winner = [
      {
        familyName: item.playerFamilyName,
        givenName: item.playerGivenName,
        nationalityCode: item.playerNationalityCode,
        playerID: item.playerId
      }
    ];

    if (item.opponent2PlayerId) {
      opponent.push({
        familyName: item.opponent2FamilyName,
        givenName: item.opponent2GivenName,
        nationalityCode: item.opponent2NationalityCode,
        playerID: item.opponent2PlayerId
      });
    }

    if (item.partnerPlayerId) {
      winner.push({
        familyName: item.partnerFamilyName,
        givenName: item.partnerGivenName,
        nationalityCode: item.partnerNationalityCode,
        playerID: item.partnerPlayerID
      });
    }

    return { ...item, matches };
  });

  const buildDate = (event: TournamentProps) => {
    return `${formatDate(event.startDate).shortNoYear} to ${
      formatDate(event.endDate).shortNoYear
    } ${new Date(event.endDate).getFullYear()}`;
  };

  const buildScores = (data: any) => {
    let allScores = data.matches
      .map((match: any) => ({ ...match, score: match.score }))
      .map((item: any) => {
        if (
          item.score !== null &&
          (item.score.includes("Default") || item.score.includes("Walk"))
        ) {
          return { score: item.score, matchTypeCode: item.matchTypeCode };
        }
        let scoreArr = (item.score || "").split(" ");
        scoreArr.pop();
        return { score: scoreArr, matchTypeCode: item.matchTypeCode };
      });

    let scores = allScores.map((scoreItem: any) => {
      return {
        matchTypeCode: scoreItem.matchTypeCode,
        score:
          scoreItem.score !== null &&
          (scoreItem.score.includes("Default") ||
            scoreItem.score.includes("Walk"))
            ? scoreItem.score
            : scoreItem.score.map((score: any) =>
                score.split("-").map((score: any) => ({
                  score:
                    score.search(/\(|\)/g) !== -1
                      ? score.substring(0, score.search(/\(|\)/g))
                      : score.replace(/\[|\]/g, ""),
                  tieBreak: score.search(/\[|\]/g) !== -1,
                  tieScore:
                    score.search(/\(|\)/g) !== -1
                      ? score.substring(
                          score.indexOf("(") + 1,
                          score.indexOf(")")
                        )
                      : ""
                }))
              )
      };
    });

    return scores;
  };

  return (
    <table
      className={`table${
        className !== undefined ? " " + className : ""
      } ${tourCode}`}
    >
      <thead>
        <tr>
          <th className="name">
            <span>Name</span>
          </th>
          <th
            onClick={() => {
              setSortCriteria({
                isAscending:
                  sortCriteria.orderFilter === START_DATE
                    ? !sortCriteria.isAscending
                    : true,
                orderFilter: START_DATE
              });
            }}
            className={`whatson-table__sort-option ${
              sortCriteria.orderFilter === START_DATE
                ? `whatson-table__sort-option--${
                    sortCriteria.isAscending ? "active-asc" : "active-desc"
                  }`
                : ""
            } date`}
          >
            <span>Date</span>
          </th>
          <th
            className={`surface ${
              sortCriteria.orderFilter === HOST_NATION
                ? `whatson-table__sort-option--${
                    sortCriteria.isAscending ? "active-asc" : "active-desc"
                  }`
                : ""
            }`}
          >
            <span>Surface</span>
          </th>
          <th
            onClick={
              matchTypeCode === "S"
                ? () => {
                    setSortCriteria({
                      isAscending:
                        sortCriteria.orderFilter === WINNER
                          ? !sortCriteria.isAscending
                          : true,
                      orderFilter: WINNER
                    });
                  }
                : () => null
            }
            className={`winner ${
              matchTypeCode === "S" ? "whatson-table__sort-option " : " "
            }${
              sortCriteria.orderFilter === WINNER
                ? `whatson-table__sort-option--${
                    sortCriteria.isAscending ? "active-asc" : "active-desc"
                  }`
                : ""
            }`}
          >
            <span>Winner</span>
          </th>
          <th className={`whatson-table__heading results`}>
            <span>Final Result</span>
          </th>
          <th
            onClick={
              matchTypeCode === "S"
                ? () => {
                    setSortCriteria({
                      isAscending:
                        sortCriteria.orderFilter === RUNNER_UP
                          ? !sortCriteria.isAscending
                          : true,
                      orderFilter: RUNNER_UP
                    });
                  }
                : () => null
            }
            className={`runner-up ${
              matchTypeCode === "S" ? "whatson-table__sort-option " : " "
            }${
              sortCriteria.orderFilter === RUNNER_UP
                ? `whatson-table__sort-option--${
                    sortCriteria.isAscending ? "active-asc" : "active-desc"
                  }`
                : ""
            }`}
          >
            <span>Runner-up</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {data !== undefined
          ? data.map((event: TournamentProps, key: number) => {
              return (
                <tr
                  key={key}
                  className={`whatson-table__tournament${
                    event.tourStatusCode == "CN" || event.tourStatusCode == "PP"
                      ? "--cancelled"
                      : ""
                  } results`}
                >
                  <td
                    className={`name${
                      event.isRecognised ? " recognised" : ""
                    } `}
                  >
                    {event.tourStatusCode !== "CN" &&
                    event.tourStatusCode !== "PP" ? (
                      <span className="name">
                        <a href={event.tournamentLink}>
                          <div className="short">
                            {event.name}
                            {event.isRecognised ? (
                              event.recognisedTournamentLink ? (
                                <a
                                  className="recognised-tournament"
                                  href={event.recognisedTournamentLink}
                                />
                              ) : (
                                <div className="recognised-tournament" />
                              )
                            ) : null}
                          </div>
                          <div className="long">
                            <span
                              className={`results itf-flags itf-flags--${event.hostNationCode} `}
                            />
                            {
                              <div>
                                {event.name}{" "}
                                {event.hostNation
                                  ? `(${omitNation(event.hostNationCode)})`
                                  : ""}
                                {event.isRecognised ? (
                                  <a
                                    href="#"
                                    className="recognised-tournament"
                                  />
                                ) : null}
                              </div>
                            }
                            <span
                              className={`surface-icon surface-icon__${
                                event.surfaceDesc
                                  ? event.surfaceDesc.toLowerCase()
                                  : ""
                              }`}
                            />
                          </div>
                        </a>
                      </span>
                    ) : (
                      <span className="name">
                        <div className="short">
                          {event.name}
                          {event.isRecognised ? (
                            <a href="#" className="recognised-tournament" />
                          ) : null}
                        </div>
                        <div className="long">
                          <span
                            className={`itf - flags itf - flags--${omitNation(
                              event.hostNationCode
                            )} `}
                          />
                          {
                            <div>
                              {event.name}{" "}
                              {event.hostNation
                                ? `(${omitNation(event.hostNationCode)})`
                                : ""}
                              {event.isRecognised ? (
                                <a href="#" className="recognised-tournament" />
                              ) : null}
                            </div>
                          }
                        </div>
                      </span>
                    )}
                    {event.tourStatusDesc !== null ? (
                      <Status code={event.tourStatusCode} />
                    ) : (event.liveLink && event.liveLink !== "") ||
                      (event.liveStreamingUrl !== "" &&
                        event.liveStreamingUrl !== null) ? (
                      <span className="status">
                        <a
                          target="_blank"
                          href={event.liveStreamingUrl || event.liveLink}
                          className="live"
                        >
                          Live
                        </a>
                      </span>
                    ) : null}
                  </td>
                  <td className="date">
                    <span className="label">Date:</span>
                    <span className="date">{buildDate(event)}</span>
                  </td>

                  <td className="date">
                    <span className="label">Surface:</span>
                    <span className="surface">
                      {event.surfaceDesc}
                      <div
                        className={`surface-icon__${
                          event.surfaceDesc
                            ? event.surfaceDesc.toLowerCase()
                            : ""
                        }`}
                      />
                    </span>
                  </td>

                  <td className="winner">
                    <span className="label">Host Nation:</span>
                    <span className="winner-name">
                      {event.matches
                        ? event.matches.map((match: MatchData, key: number) => {
                            return (
                              <div
                                key={key}
                                className="winner-name__item-container"
                              >
                                {match.winner.map(
                                  (champ: Player, key: number) => (
                                    <a
                                      href={champ.playerProfileLink}
                                      key={key}
                                      className="winner-name__item"
                                    >
                                      <div
                                        className={`itf-flags itf-flags--${champ.nationalityCode}`}
                                      />
                                      <div>{`${champ.givenName} ${champ.familyName} `}</div>
                                    </a>
                                  )
                                )}
                              </div>
                            );
                          })
                        : null}
                    </span>
                  </td>
                  <td className="score">
                    <span className="label">Score:</span>
                    <span className="score score--desktop">
                      {event.results.map((item: any, key: number) => (
                        <div key={key} className="score-value__status">
                          {item.score}
                        </div>
                      ))}
                    </span>
                    <span className="score score--mobile">
                      {buildScores(event).map((item: any, key: number) => (
                        <div
                          key={key}
                          className={`score-value__status-container${
                            item.matchTypeCode ? ` ${item.matchTypeCode}` : ""
                          }`}
                        >
                          {!Array.isArray(item.score) ? (
                            <div className="score-value__status">
                              {item.score}
                            </div>
                          ) : (
                            item.score.map((set: any) => {
                              return set.map((game: any, key: number) => (
                                <div key={key} className={`score-value`}>
                                  {game.score}
                                  {game.tieScore ? (
                                    <div className="tie-break">
                                      {game.tieScore}
                                    </div>
                                  ) : null}
                                </div>
                              ));
                            })
                          )}
                        </div>
                      ))}
                    </span>
                  </td>
                  <td className="runner-up">
                    <span className="label">Category:</span>
                    <span className="runner-up-value">
                      {event.matches
                        ? event.matches.map((match: MatchData, key: number) => {
                            return (
                              <div
                                key={key}
                                className="runner-up__item-container"
                              >
                                {match.opponent.map(
                                  (opp: Player, key: number) => (
                                    <a
                                      href={opp.playerProfileLink}
                                      key={key}
                                      className="runner-up__item"
                                    >
                                      <div
                                        className={`itf-flags itf-flags--${opp.nationalityCode}`}
                                      />
                                      <div>{`${opp.givenName} ${opp.familyName} `}</div>
                                    </a>
                                  )
                                )}
                              </div>
                            );
                          })
                        : null}
                    </span>
                  </td>
                  <MobileView matches={event.matches} />
                </tr>
              );
            })
          : null}
      </tbody>
    </table>
  );
};

export default Table;
